"use client";

import { Button } from "@/src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/src/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/src/components/ui/dialog";
import { useDebouncedValue } from "@/src/hooks/use-debounced-value";
import { useSearch } from "@/src/lib/clientApi/search";
import { kClientPaths } from "@/src/lib/clientHelpers/clientPaths";
import { cn } from "@/src/lib/utils";
import { Command as CommandPrimitive } from "cmdk";
import {
  CircleCheck,
  LayoutList,
  Loader2,
  NotebookTabs,
  SearchIcon,
} from "lucide-react";
import { useRouter } from "nextjs-toploader/app";
import { useEffect, useRef, useState } from "react";

function listBreadcrumb(params: { workspaceName: string | null }) {
  return params.workspaceName ?? "Personal";
}

function choreBreadcrumb(params: {
  listName: string;
  workspaceName: string | null;
}) {
  return params.workspaceName
    ? `${params.workspaceName} / ${params.listName}`
    : params.listName;
}

export function AppSearch() {
  const router = useRouter();
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, query.trim() ? 250 : 0);
  const {
    workspaces,
    lists,
    chores,
    choresHasMore,
    isLoading,
    isLoadingMore,
    isValidating,
    loadMoreChores,
  } = useSearch({
    query: debouncedQuery,
    enabled: open,
  });
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(/Mac|iPhone|iPad/.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open || !choresHasMore || isLoadingMore) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreChores();
        }
      },
      { root: listRef.current, rootMargin: "120px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, choresHasMore, isLoadingMore, loadMoreChores, chores.length]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const navigateTo = (href: string) => {
    handleOpenChange(false);
    router.push(href);
  };

  const hasResults =
    workspaces.length > 0 || lists.length > 0 || chores.length > 0;
  const isQueryPending = query.trim() !== debouncedQuery.trim();
  const isSearching =
    isQueryPending || isLoading || (isValidating && !isLoadingMore);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        aria-label="Search"
        className="size-8 justify-center px-0 has-[>svg]:px-2 sm:h-9 sm:w-76 sm:justify-start sm:px-3 sm:has-[>svg]:px-3 sm:text-muted-foreground max-w-full sm:max-w-full"
        onClick={() => setOpen(true)}
      >
        <SearchIcon className="size-4" />
        <span className="hidden sm:inline-flex flex-1 text-left">
          Search...
        </span>
        <kbd
          className={cn(
            "pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:inline-flex"
          )}
        >
          {isMac ? "⌘K" : "Ctrl+K"}
        </kbd>
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            "flex flex-col gap-0 overflow-hidden p-0",
            // Mobile: full-screen sheet
            "inset-0 top-0 left-0 h-dvh max-h-dvh w-full max-w-none translate-x-0 translate-y-0 rounded-none ring-0",
            // Desktop: centered palette
            "sm:inset-auto sm:top-[12vh] sm:left-1/2 sm:right-auto sm:bottom-auto sm:h-[min(36rem,80vh)] sm:max-h-[80vh] sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:translate-y-0 sm:rounded-xl sm:ring-1"
          )}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Search</DialogTitle>
            <DialogDescription>
              Search workspaces, lists, and chores
            </DialogDescription>
          </DialogHeader>
          <Command
            shouldFilter={false}
            className={cn(
              "flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-none p-0 sm:rounded-xl",
              "[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
            )}
          >
            <div
              data-slot="command-input-wrapper"
              className="flex h-12 shrink-0 items-center gap-2 border-b px-3"
            >
              {isSearching ? (
                <Loader2 className="size-4 shrink-0 animate-spin opacity-70" />
              ) : (
                <SearchIcon className="size-4 shrink-0 opacity-50" />
              )}
              <CommandPrimitive.Input
                placeholder="Search workspaces, lists, and chores..."
                value={query}
                onValueChange={setQuery}
                className="placeholder:text-muted-foreground h-10 min-w-0 flex-1 bg-transparent py-3 pr-10 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <CommandList
              ref={listRef}
              className="max-h-none min-h-0 flex-1 overflow-y-auto"
            >
              {isSearching && !hasResults ? (
                <CommandEmpty>
                  <span className="inline-flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    Searching...
                  </span>
                </CommandEmpty>
              ) : (
                <>
                  {!isSearching && !hasResults && (
                    <CommandEmpty>No results found.</CommandEmpty>
                  )}
                  {workspaces.length > 0 && (
                    <CommandGroup heading="Workspaces">
                      {workspaces.map((workspace) => (
                        <CommandItem
                          key={workspace.id}
                          value={`workspace:${workspace.id}`}
                          onSelect={() =>
                            navigateTo(
                              kClientPaths.app.workspace.single(workspace.id)
                            )
                          }
                        >
                          <NotebookTabs />
                          <span className="truncate">{workspace.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {lists.length > 0 && (
                    <CommandGroup heading="Lists">
                      {lists.map((list) => (
                        <CommandItem
                          key={list.id}
                          value={`list:${list.id}`}
                          onSelect={() =>
                            navigateTo(kClientPaths.app.list.single(list.id))
                          }
                        >
                          <LayoutList />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate">{list.name}</span>
                            <span className="text-muted-foreground truncate text-xs">
                              {listBreadcrumb({
                                workspaceName: list.workspaceName,
                              })}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {chores.length > 0 && (
                    <CommandGroup heading="Chores">
                      {chores.map((chore) => (
                        <CommandItem
                          key={chore.id}
                          value={`chore:${chore.id}`}
                          onSelect={() =>
                            navigateTo(
                              kClientPaths.app.list.chore.single(
                                chore.listId,
                                chore.id
                              )
                            )
                          }
                        >
                          <CircleCheck />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate">{chore.title}</span>
                            <span className="text-muted-foreground truncate text-xs">
                              {choreBreadcrumb({
                                listName: chore.listName,
                                workspaceName: chore.workspaceName,
                              })}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {choresHasMore && (
                    <div
                      ref={sentinelRef}
                      className="text-muted-foreground px-2 py-3 text-center text-xs"
                    >
                      {isLoadingMore ? "Loading more..." : null}
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
